import getConfiguration from '@/store/getConfiguration';
import { GetProductTypesOutputDTO, ProductType, ProductTypeApi } from '@/api';
import { showError } from '@/mixins/toastService';
import * as Sentry from '@sentry/vue';

async function getApi() {
  const configuration = await getConfiguration();
  return new ProductTypeApi(configuration, '');
}

export type ProductGroup = {
  name: string;
  value: ProductType;
};

type InitialState = {
  productGroups: GetProductTypesOutputDTO;
  fetchAbortController: AbortController | null;
  isLoading: boolean;
};

export const initialState = (): InitialState => ({
  productGroups: { product_types: [] },
  isLoading: false,
  fetchAbortController: null,
});
export type DockProductGroupsState = ReturnType<typeof initialState>;
type ActionType = {
  commit: Function;
  state: DockProductGroupsState;
};

export default {
  namespaced: true,
  state: initialState(),
  mutations: {
    resetState(state: DockProductGroupsState) {
      Object.assign(state, initialState());
    },

    setProductGroups(
      state: DockProductGroupsState,
      productGroups: GetProductTypesOutputDTO,
    ) {
      state.productGroups = productGroups;
    },
    setLoading(state: DockProductGroupsState, isLoading: boolean) {
      state.isLoading = isLoading;
    },
  },
  actions: {
    async fetchProductGroups({ commit, state }: ActionType) {
      state.fetchAbortController?.abort();
      const controller = new AbortController();
      state.fetchAbortController = controller;

      commit('setLoading', true);

      try {
        const api = await getApi();
        const { data } = await api.getProductTypesDockschedulingProductTypesGet(
          { signal: controller.signal },
        );

        // Only commit if this request wasn't aborted
        if (!controller.signal.aborted) {
          commit('setProductGroups', data);
        }
      } catch (error: any) {
        if (!controller.signal.aborted) {
          showError('dock-management.error-fetching-product-groups');
          Sentry.captureException(error);
        }
      } finally {
        if (!controller.signal.aborted) {
          commit('setLoading', false);
        }
      }
    },
  },
  getters: {
    productGroupOptions(state: DockProductGroupsState): ProductGroup[] {
      return (
        state.productGroups.product_types?.map((pg: string) => ({
          value: pg as ProductType,
          name: pg.replaceAll('_', ' '),
        })) || []
      );
    },
  },
};
